/**
 * Centralized Authentication Error Handler
 *
 * This module provides a single point of control for handling authentication failures
 * and token expiration, preventing race conditions and multiple redirect attempts.
 */

import { tokenManager, userManager } from './auth';

// Redirect state management
let isRedirecting = false;

/**
 * Initialize the auth error handler with a navigation callback.
 * This should be called once at app startup with a React Router navigate function.
 *
 * @param _navigate - React Router's navigate function (kept for future use, prefix to avoid unused warning)
 */
export function initAuthErrorHandler(_navigate: (path: string) => void): void {
  // Navigate function is kept for future use but currently we use window.location.href
  // to force a full page reload which ensures clean state
}

/**
 * Check if currently in the process of redirecting to login.
 * This prevents multiple simultaneous redirect attempts.
 */
export function getIsRedirecting(): boolean {
  return isRedirecting;
}

/**
 * Handle authentication failure by cleaning up local state and redirecting to login.
 * This function is idempotent - multiple calls will only result in one redirect.
 *
 * @param reason - Optional reason for the auth failure (for logging)
 */
export function handleAuthFailure(reason?: string): void {
  // Prevent multiple simultaneous redirects
  if (isRedirecting) {
    console.debug(
      '[AuthErrorHandler] Already redirecting, skipping duplicate auth failure handling'
    );
    return;
  }

  isRedirecting = true;

  // Log the failure for debugging
  if (reason) {
    console.warn(`[AuthErrorHandler] Auth failure: ${reason}`);
  } else {
    console.warn('[AuthErrorHandler] Auth failure detected');
  }

  // Clean up local authentication state
  try {
    tokenManager.clearTokens();
    userManager.clearUser();

    // Also clear localStorage directly to be absolutely sure
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_data');

    console.debug('[AuthErrorHandler] Cleared authentication state');
  } catch (error) {
    console.error('[AuthErrorHandler] Error clearing auth state:', error);
  }

  // Perform redirect
  const loginPath = '/login';
  const currentPath = window.location.pathname;

  // Check if we're already on the login page
  if (currentPath === loginPath) {
    console.debug('[AuthErrorHandler] Already on login page, skipping redirect');
    // Still need to update the redirecting flag logic
    setTimeout(() => {
      isRedirecting = false;
      console.debug('[AuthErrorHandler] Reset redirect flag');
    }, 1000);
    return;
  }

  // Use window.location.href to force a full page reload
  // This ensures the AuthContext state is completely reset and prevents race conditions
  // with PublicRoute checking isAuthenticated before the state update propagates
  console.debug('[AuthErrorHandler] Redirecting to login with full page reload');
  window.location.href = loginPath;

  // Reset the flag after a delay to allow for future auth failures
  // (e.g., if user tries to login again with wrong credentials)
  setTimeout(() => {
    isRedirecting = false;
    console.debug('[AuthErrorHandler] Reset redirect flag');
  }, 1000);
}

/**
 * Reset the redirecting flag (useful for testing or manual recovery).
 * This is exported for testing purposes but shouldn't be needed in normal operation.
 */
export function resetRedirectingFlag(): void {
  isRedirecting = false;
}

/**
 * Token refresh state management
 * Prevents multiple concurrent token refresh attempts
 */
let isRefreshing = false;
let refreshPromise: Promise<string> | null = null;

/**
 * Refresh access token using refresh token.
 * Multiple concurrent calls will receive the same refresh promise.
 *
 * @param refreshFn - Function that performs the actual token refresh API call
 * @returns Promise that resolves to the new access token
 */
export async function refreshAccessTokenWithDedup(
  refreshFn: () => Promise<{ access_token: string; refresh_token?: string }>
): Promise<string> {
  // If already refreshing, return the existing promise
  if (isRefreshing && refreshPromise) {
    console.debug('[AuthErrorHandler] Token refresh already in progress, waiting...');
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const refreshToken = tokenManager.getRefreshToken();
      if (!refreshToken) {
        // No refresh token available - user needs to login again
        console.debug('[AuthErrorHandler] No refresh token available');
        handleAuthFailure('No refresh token available');
        throw new Error('No refresh token available');
      }

      console.debug('[AuthErrorHandler] Attempting token refresh...');
      const response = await refreshFn();
      const { access_token, refresh_token: newRefreshToken } = response;

      // Update tokens in localStorage
      tokenManager.setTokens(access_token, newRefreshToken || refreshToken);

      console.debug('[AuthErrorHandler] Token refresh successful');
      return access_token;
    } catch (error: any) {
      console.error('[AuthErrorHandler] Token refresh failed:', error);
      // Check if this is a 401 error from the refresh endpoint
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        handleAuthFailure('401 Invalid token: Signature has expired - refresh token also expired');
      } else {
        handleAuthFailure('Token refresh failed');
      }
      throw error;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/**
 * Check if currently refreshing token
 */
export function getIsRefreshing(): boolean {
  return isRefreshing;
}

export default {
  initAuthErrorHandler,
  handleAuthFailure,
  getIsRedirecting,
  resetRedirectingFlag,
  refreshAccessTokenWithDedup,
  getIsRefreshing,
};
